import { useMemo } from "react";
import { useProductVariants, useProductVariantById } from "../../hooks/useProduct";
import type { ProductVariantDto } from "../../../../api";

/**
 * Resolve up to three IDs into ProductVariantDto entries for the compare page.
 *
 * IDs may be product template IDs (the common case — list views, similar
 * products) OR variant IDs (e.g. older AI chat widgets whose saved JSON only
 * carries variantId, not productId). We try the template lookup first; if it
 * 404s, we fall back to the variant-by-id endpoint for the same slot. SWR
 * de-duplicates within a render, so the worst case is one extra request per
 * legacy slot.
 */
export function useProductComparison(ids: number[]) {
  const t1 = useProductVariants(ids[0] || 0);
  const t2 = useProductVariants(ids[1] || 0);
  const t3 = useProductVariants(ids[2] || 0);

  // Only attempt the variant lookup once the template lookup has actually
  // errored — passing 0 keeps the SWR key null so no request is made on the
  // happy path.
  const v1 = useProductVariantById(t1.error && ids[0] ? ids[0] : 0);
  const v2 = useProductVariantById(t2.error && ids[1] ? ids[1] : 0);
  const v3 = useProductVariantById(t3.error && ids[2] ? ids[2] : 0);

  const slots = useMemo(
    () =>
      [
        { template: t1, variant: v1 },
        { template: t2, variant: v2 },
        { template: t3, variant: v3 },
      ].slice(0, ids.length),
    [t1, t2, t3, v1, v2, v3, ids.length],
  );

  const isLoading = slots.some(
    (s) => s.template.isLoading || (s.template.error && s.variant.isLoading),
  );
  const hasError = slots.every((s) => s.template.error && s.variant.error);

  const products = useMemo(
    () =>
      slots
        .map(({ template, variant }) => {
          if (template.data) {
            const v = template.data.variants?.[0];
            if (v) return v;
            const tpl = template.data.product;
            if (tpl) {
              return {
                ...tpl,
                productTemplateId: tpl.id,
              } as unknown as ProductVariantDto;
            }
          }
          if (variant.data) return variant.data;
          return undefined;
        })
        .filter((p): p is ProductVariantDto => p !== undefined),
    [slots],
  );

  return { isLoading, hasError, products };
}
